/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { cloudProviderNames } from '@app/cluster-management/constants';
import { bytesToGb } from '@app/domain/information-units';
import { assertNever } from '@shared/domain/assert';
import {
  CloudProvider,
  NebulaClusterSize,
  NebulaClusterVersion,
  NebulaPrice,
  NebulaSubscriptionInfo,
  NebulaSubscriptionStatus,
} from '@shared/types/nebula';

export const canParseProductMainInfo = (value: string): boolean => {
  try {
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue.every((item) => typeof item === 'string') : false;
  } catch (e) {
    return false;
  }
};

export const parseProductMainInfo = (value: string): string[] => JSON.parse(value);

export const getCloudProvider = (provider?: CloudProvider) =>
  cloudProviderNames.find(({ value }) => value === provider)?.label;

export const getProviderLogo = (provider?: CloudProvider) => {
  switch (provider) {
    case CloudProvider.AWS:
      return 'ggcc:nebula-aws-logo';
    case CloudProvider.AZURE:
      return 'ggcc:nebula-azure-logo';
    case CloudProvider.GCE:
      return 'ggcc:nebula-cloud-logo';
    default:
      return '';
  }
};

export const getNodeSize = (clusterSize: NebulaClusterSize) => {
  switch (clusterSize) {
    case NebulaClusterSize.S:
      return 'Small';
    case NebulaClusterSize.M:
      return 'Medium';
    case NebulaClusterSize.L:
      return 'Large';
    default:
      return assertNever(clusterSize);
  }
};

export const MIN_CLUSTER_SIZE_2X = 2;
export const MIN_CLUSTER_SIZE_3X = 3;

export const MAX_CLUSTER_SIZE_2X = 16;
export const MAX_CLUSTER_SIZE_3X = 15;

// As described at https://ggsystems.atlassian.net/wiki/spaces/GMC/pages/3168010241/Nebula+2.0+MVP+Available+Instances+and+Sizing#Cluster-Sizing-Calculator
export const suggestSuitableSizeAndNodeCount = (
  requiredStorageInGb: number,
  prices: NebulaPrice[],
  product: NebulaClusterVersion,
): { clusterSize: NebulaClusterSize; nodeCount: number } | null => {
  if (requiredStorageInGb <= 0) return null;
  const EFFECTIVE_STORAGE_RATIO = 0.35;
  const MIN_NODES = product === NebulaClusterVersion.GG8 ? MIN_CLUSTER_SIZE_2X : MIN_CLUSTER_SIZE_3X;
  const MAX_NODES = product === NebulaClusterVersion.GG8 ? MAX_CLUSTER_SIZE_2X : MAX_CLUSTER_SIZE_3X;
  const totalRamNeededInGb = requiredStorageInGb / EFFECTIVE_STORAGE_RATIO;
  const priceSmall = prices.find((p) => p.clusterSize === NebulaClusterSize.S);
  const priceMedium = prices.find((p) => p.clusterSize === NebulaClusterSize.M);
  const priceLarge = prices.find((p) => p.clusterSize === NebulaClusterSize.L);
  if (!priceSmall || !priceMedium || !priceLarge) return null;
  const estimatedNodeCountSmall = Math.ceil(totalRamNeededInGb / bytesToGb(priceSmall.totalMemoryInBytes));
  const estimatedNodeCountMedium = Math.ceil(totalRamNeededInGb / bytesToGb(priceMedium.totalMemoryInBytes));
  const estimatedNodeCountLarge = Math.ceil(totalRamNeededInGb / bytesToGb(priceLarge.totalMemoryInBytes));
  if (estimatedNodeCountSmall < 3) return { clusterSize: priceSmall.clusterSize, nodeCount: MIN_NODES };
  if (estimatedNodeCountSmall <= 7) return { clusterSize: priceSmall.clusterSize, nodeCount: estimatedNodeCountSmall };
  if (estimatedNodeCountMedium <= 7)
    return { clusterSize: priceMedium.clusterSize, nodeCount: estimatedNodeCountMedium };
  // Suggest MAX_NODES even if MAX_NODES is not enough to fit the data. This is by design.
  return {
    clusterSize: priceLarge.clusterSize,
    nodeCount: Math.min(MAX_NODES, estimatedNodeCountLarge),
  };
};

export const getClusterTypeTooltip = (isNebula: boolean) => (isNebula ? 'Fully-Managed by GridGain' : 'Attached');

export const isSubscriptionUnpaid = (subscription: NebulaSubscriptionInfo | null) => {
  if (!subscription) return true;
  return subscription.status === NebulaSubscriptionStatus.UNPAID;
};
